import { getRouteApi, Link } from '@tanstack/react-router'
import { ChevronDown } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Column } from '~/app/AppShell'
import { ItemIcon } from '~/components/ItemIcon'
import { loadDataset, loadDisplayIndex } from '~/lib/data'
import { useDocumentTitle } from '~/lib/head'
import { doneIn, setDone } from '~/lib/progress'
import { iconKeyFor } from '~/lib/search'
import { useData } from '~/lib/use-data'

/**
 * Which sets are folded shut, remembered across visits — 82 sets is a long
 * scroll, and re-collapsing them on every visit would make the fold useless.
 * Stored as a plain id list; a corrupt value reads as "nothing collapsed".
 */
const COLLAPSED_KEY = 'mistria-codex:museum-collapsed'
/**
 * The gaps-only filter is a preference, like the fold: it changes which rows
 * you're shown, not which museum this is, so it lives beside the fold in
 * localStorage rather than in the URL — and survives leaving and coming back.
 */
const GAPS_ONLY_KEY = 'mistria-codex:museum-gaps-only'

function readGapsOnly(): boolean {
  try {
    return localStorage.getItem(GAPS_ONLY_KEY) === 'true'
  } catch {
    return false
  }
}

function writeGapsOnly(value: boolean): void {
  try {
    if (value) localStorage.setItem(GAPS_ONLY_KEY, 'true')
    else localStorage.removeItem(GAPS_ONLY_KEY)
  } catch {
    // Private mode: the filter still works for this session via state.
  }
}

function readCollapsed(): Set<string> {
  try {
    const raw = JSON.parse(localStorage.getItem(COLLAPSED_KEY) ?? '[]') as unknown
    return new Set(Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string') : [])
  } catch {
    return new Set()
  }
}

function writeCollapsed(ids: Set<string>): void {
  try {
    localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...ids].sort()))
  } catch {
    // Private mode: the fold still works for this session via state.
  }
}

const route = getRouteApi('/museum')

/**
 * The museum, and what it is still missing.
 *
 * Progress lives on the device, in one flat Dexie table, and the checkbox writes
 * a timestamp rather than a boolean — see `lib/progress.ts` for why that makes
 * the whole thing a CRDT before any sync code exists.
 *
 * The gap view is the point. A list of what you have is a trophy cabinet; a list
 * of what you are missing is a plan for the afternoon, and it is what the Today
 * screen will cross-reference at A5's other half.
 */

interface MuseumSet {
  id: string
  name: string
  /** `museum/<wing>` — one sprite per wing, shared by its sets. */
  icon_key: string | null
  wing: string
  item_ids: string[]
  required_count: number
  display_order: number
}

const WINGS = [
  { id: 'archaeology', label: 'Archaeology' },
  { id: 'fish', label: 'Fish' },
  { id: 'insects', label: 'Insects' },
  { id: 'flora', label: 'Flora' },
] as const

export function MuseumRoute() {
  useDocumentTitle('Museum')
  // Wing and filter live in the URL, so back from an item page restores the
  // same tab, the same rows, and (with the data cache) the same scroll.
  const searchParams = route.useSearch()
  const navigate = route.useNavigate()
  const wing = searchParams.wing ?? 'archaeology'
  const query = searchParams.q ?? ''
  const setWing = (w: string): void =>
    void navigate({
      search: ({ wing: _, ...rest }) => (w === 'archaeology' ? rest : { ...rest, wing: w }),
      replace: true,
    })
  const setQuery = (q: string): void =>
    void navigate({
      search: ({ q: _, ...rest }) => (q === '' ? rest : { ...rest, q }),
      replace: true,
    })

  // The almanac halves are cached for the back button; the ticks are read
  // fresh every mount — they change while the app runs (including on the
  // item pages, which write the same museum:<id> keys).
  const { data } = useData('museum', async () => {
    const [sets, index] = await Promise.all([
      loadDataset<MuseumSet>('museum_sets'),
      loadDisplayIndex(),
    ])
    return { sets, index }
  })
  const sets = data?.sets ?? null
  const index = data?.index ?? {}
  const [donated, setDonated] = useState<Set<string>>(new Set())
  const [gapsOnly, setGapsOnlyState] = useState(() => readGapsOnly())
  const setGapsOnly = (value: boolean): void => {
    writeGapsOnly(value)
    setGapsOnlyState(value)
  }
  const [collapsed, setCollapsed] = useState<Set<string>>(() => readCollapsed())

  const toggleCollapsed = (setId: string): void => {
    setCollapsed((current) => {
      const next = new Set(current)
      if (next.has(setId)) next.delete(setId)
      else next.add(setId)
      writeCollapsed(next)
      return next
    })
  }
  const setAllCollapsed = (fold: boolean): void => {
    const next = fold ? new Set((sets ?? []).map((s) => s.id)) : new Set<string>()
    writeCollapsed(next)
    setCollapsed(next)
  }

  useEffect(() => {
    let live = true
    doneIn('museum').then((done) => live && setDonated(done))
    return () => {
      live = false
    }
  }, [])

  const toggle = (id: string): void => {
    const next = new Set(donated)
    const nowDone = !next.has(id)
    if (nowDone) next.add(id)
    else next.delete(id)
    // Optimistic: the write is local and cannot meaningfully fail, and waiting
    // on IndexedDB to tick a checkbox is a visible stutter on a phone.
    setDonated(next)
    void setDone('museum', id, nowDone)
  }

  const totals = useMemo(() => {
    const all = (sets ?? []).flatMap((s) => s.item_ids)
    return { have: all.filter((id) => donated.has(id)).length, total: all.length }
  }, [sets, donated])

  const needle = query.trim().toLowerCase()
  const matches = useMemo(() => {
    if (needle === '') return null
    // Searching cuts across every wing at once — a player looking for the
    // Ancient Sword should not need to know which wing owns it first.
    const hit = new Set<string>()
    for (const set of sets ?? []) {
      for (const id of set.item_ids) {
        const name = index[id]?.n ?? id.replace(/_/g, ' ')
        if (name.toLowerCase().includes(needle)) hit.add(id)
      }
    }
    return hit
  }, [sets, index, needle])

  const shown = useMemo(
    () =>
      (sets ?? [])
        .filter((s) =>
          matches === null ? s.wing === wing : s.item_ids.some((id) => matches.has(id)),
        )
        .filter((s) => !gapsOnly || s.item_ids.some((id) => !donated.has(id)))
        .sort((a, b) => a.display_order - b.display_order || a.name.localeCompare(b.name)),
    [sets, wing, gapsOnly, donated, matches],
  )

  if (sets === null) {
    return (
      <Column>
        <p className="text-ink-mute text-sm">Loading the museum…</p>
      </Column>
    )
  }

  return (
    <Column>
      <header>
        <h1 className="text-2xl">Museum</h1>
        <p className="mt-1 text-ink-mute text-sm">
          <span data-numeral>{totals.have}</span> of <span data-numeral>{totals.total}</span>{' '}
          donated across {sets.length} sets. Kept on this device.
        </p>
      </header>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1">
          {WINGS.map((w) => {
            const items = sets.filter((s) => s.wing === w.id).flatMap((s) => s.item_ids)
            const have = items.filter((id) => donated.has(id)).length
            return (
              <button
                key={w.id}
                type="button"
                onClick={() => setWing(w.id)}
                className="tap-target flex items-center gap-1.5 rounded-pill border border-rule py-1 pr-2.5 pl-1 text-xs transition-colors"
                style={
                  wing === w.id
                    ? { background: 'var(--museum-tint)', color: 'var(--ink)', fontWeight: 600 }
                    : { color: 'var(--ink-mute)' }
                }
              >
                <ItemIcon iconKey={`museum/${w.id}`} name={w.label} size="sm" />
                {w.label}{' '}
                <span data-numeral>
                  {have}/{items.length}
                </span>
              </button>
            )
          })}
        </div>

        <label className="flex items-center gap-1.5 text-ink-mute text-xs">
          <input
            type="checkbox"
            checked={gapsOnly}
            onChange={(event) => setGapsOnly(event.target.checked)}
          />
          Only what is missing
        </label>

        <div className="flex items-center gap-2 text-xs">
          <button
            type="button"
            onClick={() => setAllCollapsed(true)}
            className="tap-target text-ink-faint underline decoration-rule underline-offset-4 hover:text-ink"
          >
            Collapse all
          </button>
          <button
            type="button"
            onClick={() => setAllCollapsed(false)}
            className="tap-target text-ink-faint underline decoration-rule underline-offset-4 hover:text-ink"
          >
            Expand all
          </button>
        </div>
      </div>

      <input
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Find an item across every wing"
        aria-label="Filter museum items"
        className="mt-3 w-full rounded-tile border border-rule bg-surface px-3 py-2 text-ink text-sm placeholder:text-ink-faint"
      />

      <div className="mt-4 flex flex-col gap-5">
        {shown.map((set) => {
          const have = set.item_ids.filter((id) => donated.has(id)).length
          const complete = have >= set.required_count
          // A search overrides the fold: hiding a hit inside a collapsed set
          // would read as "not in the museum", which is a lie.
          const folded = collapsed.has(set.id) && matches === null
          return (
            <section key={set.id}>
              <h2>
                <button
                  type="button"
                  onClick={() => toggleCollapsed(set.id)}
                  aria-expanded={!folded}
                  className="tap-target flex w-full items-center gap-2 text-left font-display font-semibold text-ink text-sm"
                >
                  <ChevronDown
                    aria-hidden
                    size={14}
                    strokeWidth={2}
                    className={`shrink-0 text-ink-faint transition-transform ${folded ? '-rotate-90' : ''}`}
                  />
                  {/* The wing, not the set: 82 sets share four sprites, and
                      which wing a set belongs to is the thing a folded row
                      stops telling you. */}
                  <ItemIcon
                    iconKey={set.icon_key ?? `museum/${set.wing}`}
                    name={`${set.wing} wing`}
                    size="sm"
                  />
                  {set.name}
                  <span
                    data-numeral
                    className="font-normal text-xs"
                    style={{ color: complete ? 'var(--museum)' : 'var(--ink-faint)' }}
                  >
                    {have}/{set.required_count}
                  </span>
                </button>
              </h2>

              {folded ? null : (
                <ul className="mt-1.5 flex flex-col divide-y divide-rule border-rule border-y">
                  {set.item_ids
                    .filter((id) => matches === null || matches.has(id))
                    .filter((id) => !gapsOnly || !donated.has(id))
                    .map((id) => {
                      const entry = index[id]
                      const isDone = donated.has(id)
                      return (
                        <li key={id} className="flex items-center gap-3 py-2">
                          <input
                            type="checkbox"
                            checked={isDone}
                            onChange={() => toggle(id)}
                            aria-label={`${entry?.n ?? id} donated`}
                          />
                          <ItemIcon
                            iconKey={iconKeyFor(id, entry)}
                            name={entry?.n ?? id}
                            size="sm"
                          />
                          {/*
                            One link, not two. This row used to carry a
                            separate "where?" shortcut to the reverse-lookup
                            screen; that screen is now the item page's first
                            section, so the shortcut and the name pointed at
                            the same URL — and two links to one place in a
                            three-element row is noise, not an affordance.
                          */}
                          <Link
                            to="/item/$id"
                            params={{ id }}
                            className="min-w-0 flex-1 truncate text-sm"
                            style={{
                              color: isDone ? 'var(--ink-faint)' : 'var(--ink)',
                              textDecoration: isDone ? 'line-through' : 'none',
                            }}
                          >
                            {entry?.n ?? id.replace(/_/g, ' ')}
                          </Link>
                        </li>
                      )
                    })}
                </ul>
              )}
            </section>
          )
        })}
      </div>

      {shown.length === 0 && (
        <p className="mt-6 text-ink-mute text-sm">
          Nothing missing in this wing. That is the whole wing done.
        </p>
      )}

      <p className="mt-6 text-ink-faint text-xs leading-relaxed">
        Progress is saved on this device and never sent anywhere on its own. To carry it to a second
        device, create a sync code in{' '}
        <Link to="/settings" className="underline decoration-rule underline-offset-4">
          Settings
        </Link>
        .
      </p>
    </Column>
  )
}
